import { BrowserProvider } from "ethers";
import posthog from 'posthog-js';
import React, { useEffect, useState } from 'react';
import { API_URL } from '../config';
import { useAuth } from "../contexts/AuthContext";
import { magic } from "../lib/magic";
import { placeParlayOnchain } from "../lib/onchain";
import LoadingDots from './LoadingDots';
import LoginModal from './LoginModal';
import './TotalAmountModal.css';

interface ParlayLeg {
  eventTitle: string;
  marketQuestion: string;
  outcome: 'yes' | 'no';
  outcomePrice: number;
  decimalOdds: number;
  endDate?: string;
  marketIcon?: string;
  marketId: string;
  sportsTeamData?: any;
}

interface TotalAmountModalProps {
  isOpen: boolean;
  onClose: () => void;
  parlayLegs: ParlayLeg[];
  onViewOdds: (totalAmount: number) => Promise<boolean>;
  onConfirm: () => void;
  isLoading: boolean;
  formatNumberWithCommas: (num: number) => string;
  calculateSettlementDate: (legs: ParlayLeg[]) => string;
  currentQuote?: {
    quote_id: string;
    total_odds: number;
    expected_payout: number;
    expiry: string;
    total_amount: number;
  } | null;
  onRemoveLegs?: (indices: number[]) => void;
}

const TotalAmountModal: React.FC<TotalAmountModalProps> = ({
  isOpen,
  onClose,
  parlayLegs,
  onViewOdds,
  onConfirm,
  isLoading,
  formatNumberWithCommas,
  calculateSettlementDate,
  currentQuote,
  onRemoveLegs
}) => {
  const { isAuthenticated, user } = useAuth();
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [showToWin, setShowToWin] = useState<boolean>(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isQuoting, setIsQuoting] = useState(false);
  const [isPlacing, setIsPlacing] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [selectedLegIndices, setSelectedLegIndices] = useState<Set<number>>(new Set());
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [stakeModifiedAfterQuote, setStakeModifiedAfterQuote] = useState<boolean>(false);

  // Reset amount when modal opens
  useEffect(() => {
    if (isOpen) {
      setTotalAmount(0);
      setShowToWin(false);
      setErrorMsg(null);
      setSelectedLegIndices(new Set());
      setIsEditMode(false);
      setStakeModifiedAfterQuote(false);
      try {
        posthog.capture('parlay:total_amount_modal_opened', {
          legs_count: parlayLegs.length,
        });
      } catch {}
    }
  }, [isOpen]);

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      document.body.classList.add('modal-open-total-amount');
    } else {
      document.body.style.overflow = 'unset';
      document.body.classList.remove('modal-open-total-amount');
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
      document.body.classList.remove('modal-open-total-amount');
    };
  }, [isOpen, onClose]);

  // Display cents; avoid rounding extremes
  // - If price is >= 0.99 and < 1.0, show two-decimal cents via truncation (e.g., 99.95¢)
  // - If price is > 0 and < 0.01, show two-decimal cents via truncation (e.g., 0.45¢), min 0.01¢ for non-zero
  // - Otherwise keep existing integer-cent rounding behavior
  const formatPrice = (price: number) => {
    const p = price || 0;
    if (p > 0 && p < 0.01) {
      const cents = Math.floor(p * 10000) / 100;
      const val = cents <= 0 ? 0.01 : cents;
      return val.toFixed(2);
    }
    if (p >= 0.99 && p < 1) {
      const truncatedTwoDecimals = Math.floor(p * 10000) / 100;
      return truncatedTwoDecimals.toFixed(2);
    }
    return Math.round(p * 100).toString();
  };

  async function getWalletAddressFallback(): Promise<string | undefined> {
    try {
      const provider = new BrowserProvider(magic.rpcProvider as any);
      const signer = await provider.getSigner();
      return await signer.getAddress();
    } catch {
      return undefined;
    }
  }

  function toEngineLegs(source: ParlayLeg[]) {
    return source.map(l => ({
      ingest_market_id: l.marketId,
      outcome_label: l.outcome.toLowerCase() === "yes" ? "Yes" : "No"
    }));
  }

  async function requestQuote(stake: number) {
    let walletAddr = user?.walletAddress || (await getWalletAddressFallback());
    
    // If gas sponsorship is available, use smart account address for quotes
    if (user?.gasSponsored && user?.smartAccountAddress) {
      walletAddr = user.smartAccountAddress;
      console.log('🚀 Using smart account address for quote:', walletAddr);
      
      // Double-check that the smart account address matches what we're using
      if (user.smartAccountAddress !== walletAddr) {
        console.error('❌ Smart account address mismatch in user object');
        throw new Error('Smart account address mismatch. Please refresh the page and try again.');
      }
    }
    
    if (!walletAddr) throw new Error("No wallet address found.");
    if (!user?.issuer) throw new Error("Missing user issuer for quoting.");

    const payload = {
      issuer: user.issuer,
      smart_contract_wallet_address: walletAddr,
      stake,
      legs: toEngineLegs(parlayLegs)
    };

    const res = await fetch(`${API_URL}/quote`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(`/quote failed: ${text}`);
    }
    return res.json();
  }

  const handleViewOdds = async () => {
    if (totalAmount <= 0) {
      alert('Please enter a total amount');
      return;
    }

    if (!isAuthenticated) {
      setIsLoginModalOpen(true);
      return;
    }

    const success = await onViewOdds(totalAmount);
    if (success) {
      setShowToWin(true);
      setStakeModifiedAfterQuote(false);
      try {
        posthog.capture('parlay:quote_requested', {
          stake: totalAmount,
          legs_count: parlayLegs.length,
        });
      } catch {}
    } else {
      alert('Failed to get quote');
    }
  };

  const handleConfirm = async () => {
    setErrorMsg(null);

    if (!isAuthenticated) {
      setIsLoginModalOpen(true);
      return;
    }

    if (!currentQuote) {
      setErrorMsg("No quote available");
      return;
    }

    let finalQuoteResp: any = null;
    
    try {
      setIsQuoting(true);
      try { posthog.capture('parlay:placing_start', { quote_stake: currentQuote?.total_amount, legs_count: parlayLegs.length }); } catch {}
      const quoteResp = await requestQuote(currentQuote.total_amount);
      setIsQuoting(false);

      if (!quoteResp?.quote_struct || !quoteResp?.houseSig) {
        throw new Error("Backend did not return quote_struct/houseSig.");
      }

      const currentTimestamp = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = quoteResp.quote_struct.expiry - currentTimestamp;

      let placed: any = null;
      finalQuoteResp = quoteResp;

      if (timeUntilExpiry < 30) {
        const freshQuoteResp = await requestQuote(currentQuote.total_amount);
        finalQuoteResp = freshQuoteResp;
      }

      setIsPlacing(true);
      
      try {
        placed = await placeParlayOnchain(finalQuoteResp);
      } catch (blockchainError: any) {
        // Don't record failed transactions - let the quote expire naturally
        // Better error messages for the user
        const errorMsg = blockchainError?.message || "Transaction failed";
        if (errorMsg.includes("user rejected") || errorMsg.includes("User denied")) {
          throw new Error("Transaction cancelled. You can try again.");
        } else if (errorMsg.includes("insufficient")) {
          throw new Error("Insufficient balance for transaction");
        } else if (errorMsg.includes("quote used")) {
          throw new Error("Quote already used. Please request a new quote.");
        } else if (errorMsg.includes("execution reverted")) {
          throw new Error("Transaction failed. Please try again.");
        } else if (blockchainError.name === 'GasSponsorshipError' || errorMsg.includes("Gas sponsorship failed")) {
          throw new Error("Gas sponsorship failed after multiple attempts. Please try again.");
        }
        throw blockchainError;
      }

      // Only record successful transactions
      // Include legs with sportsTeamData for proper icon display
      const legsWithSportsData = parlayLegs.map(leg => ({
        ingest_market_id: leg.marketId,
        outcome_label: leg.outcome.toLowerCase() === 'yes' ? 'Yes' : 'No',
        sports_team_data: leg.sportsTeamData || null
      }));

      await fetch(`${API_URL}/tickets/record`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          quoteId: finalQuoteResp.quoteId,
          txHash: placed?.txHash || null,
          legs: legsWithSportsData
        })
      });

      onConfirm();
      try { posthog.capture('parlay:placed', { quote_id: finalQuoteResp.quoteId, tx_hash: placed?.txHash || null, legs_count: parlayLegs.length, total_amount: finalQuoteResp.quote_struct?.stake || currentQuote.total_amount, total_odds: finalQuoteResp.total_odds || currentQuote.total_odds, expected_payout: finalQuoteResp.expected_payout || currentQuote.expected_payout }); } catch {}
      onClose();
    } catch (e: any) {
      console.error(e);
      setErrorMsg(e?.message || "Failed to place parlay. Please try again.");
      try { posthog.capture('parlay:placing_failed', { error: e?.message || 'unknown', legs_count: parlayLegs.length, quote_id: finalQuoteResp?.quoteId }); } catch {}
    } finally {
      setIsQuoting(false);
      setIsPlacing(false);
    }
  };

  // Emit event when quote details become visible (to-win)
  useEffect(() => {
    if (showToWin && currentQuote) {
      try {
        posthog.capture('parlay:quote_shown', {
          expected_payout: currentQuote.expected_payout,
          total_odds: currentQuote.total_odds,
          stake: currentQuote.total_amount,
          legs_count: parlayLegs.length,
        });
      } catch {}
    }
  }, [showToWin, currentQuote, parlayLegs.length]);

  const handleLoginSuccess = async () => {
    setIsLoginModalOpen(false);
    if (!showToWin) {
      const success = await onViewOdds(totalAmount);
      if (success) {
        setShowToWin(true);
      }
    } else {
      handleConfirm();
    }
  };

  const handleToggleLegSelection = (index: number) => {
    setSelectedLegIndices(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const handleRemoveLegs = () => {
    if (selectedLegIndices.size === 0 || !onRemoveLegs) return;

    // Calculate remaining legs
    const remainingCount = parlayLegs.length - selectedLegIndices.size;

    // Call parent callback to update state
    onRemoveLegs(Array.from(selectedLegIndices));

    // Close modal only if no legs remain
    if (remainingCount === 0) {
      onClose();
    }
  };

  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
    try {
      posthog.capture('parlay:edit_parlay_clicked', {
        entering_edit_mode: !isEditMode,
        legs_count: parlayLegs.length,
      });
    } catch {}
    if (isEditMode) {
      // Exiting edit mode - clear selections
      setSelectedLegIndices(new Set());
    }
  };

  if (!isOpen) return null;

  const showCheckboxes = parlayLegs.length >= 1 && isEditMode;
  const hasSelections = selectedLegIndices.size > 0;
  const remainingLegsAfterRemoval = parlayLegs.length - selectedLegIndices.size;
  const isInvalidParlayState = !hasSelections && parlayLegs.length === 1;

  return (
    <div className="total-amount-modal-overlay" onClick={(e) => {
      if (e.target === e.currentTarget) {
        try { posthog.capture('parlay:total_amount_modal_closed', { reason: 'overlay' }); } catch {}
        onClose();
      }
    }}>
      <div className="total-amount-modal-content">
        <button
          className="total-amount-modal-close-btn"
          onClick={() => { try { posthog.capture('parlay:total_amount_modal_closed', { reason: 'close_button' }); } catch {} onClose(); }}
        >
          ×
        </button>

        <div className="total-amount-modal-header">
          <div className="total-amount-modal-title">
            <div className="total-amount-modal-question">Complete Your Parlay</div>
            <div className="total-amount-modal-selection">
              {parlayLegs.length} leg{parlayLegs.length !== 1 ? 's' : ''} added
            </div>
            <div className="total-amount-modal-settlement">
              Settlement Date: {calculateSettlementDate(parlayLegs)}
            </div>
            {parlayLegs.length >= 1 && (
              <button
                className="edit-mode-btn"
                onClick={toggleEditMode}
              >
                {isEditMode ? 'Restore parlay' : 'Edit parlay'}
              </button>
            )}
          </div>
        </div>

        {/* Display Selected Legs */}
        <div className="selected-legs-section">
          {parlayLegs.map((leg, index) => {
            // Determine icon source: sports team logo or market icon
            const isSportsLeg = leg.sportsTeamData;
            let displayIcon = leg.marketIcon;
            let isDrawBet = false;

            if (isSportsLeg) {
              // Check if this is a Draw bet
              isDrawBet = leg.sportsTeamData.selectedTeam === 0;

              if (!isDrawBet) {
                // For team bets, always use the selectedTeam from sportsTeamData
                // selectedTeam: 1 = team1, 2 = team2
                const selectedTeam = leg.sportsTeamData.selectedTeam === 1
                  ? leg.sportsTeamData.team1
                  : leg.sportsTeamData.team2;
                displayIcon = selectedTeam.logo || leg.marketIcon;
              }
            }

            const isSelected = selectedLegIndices.has(index);

            return (
              <div key={`${leg.marketId}-${index}`} className={`leg-display ${showCheckboxes ? 'has-checkboxes' : ''} ${isSelected ? 'selected' : ''}`}>
                <div className={`leg-display-header ${showCheckboxes ? 'with-checkbox' : ''}`}>
                  {showCheckboxes && (
                    <div className="leg-checkbox-container">
                      <input
                        type="checkbox"
                        className="leg-checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleLegSelection(index)}
                      />
                    </div>
                  )}
                  {isDrawBet ? (
                    <div className="leg-icon-draw-container">
                      {leg.sportsTeamData.team1?.logo && (
                        <img src={leg.sportsTeamData.team1.logo} alt={leg.sportsTeamData.team1.name} className="leg-icon leg-icon-draw" />
                      )}
                      {leg.sportsTeamData.team2?.logo && (
                        <img src={leg.sportsTeamData.team2.logo} alt={leg.sportsTeamData.team2.name} className="leg-icon leg-icon-draw" />
                      )}
                    </div>
                  ) : displayIcon ? (
                    <img src={displayIcon} alt={leg.eventTitle} className="leg-icon" />
                  ) : null}
                  <div className="leg-info">
                    <div className="leg-title">{leg.eventTitle.replace(/ICC\s+\w+:\s*/i, '')}</div>
                    <div className="leg-question">{leg.marketQuestion}</div>
                  </div>
                  <div className="leg-outcome-price">
                    {formatPrice(leg.outcomePrice)}¢
                  </div>
                  <div className="leg-outcome-single">
                    {(() => {
                      const isSportsLeg = !!leg.sportsTeamData;
                      // For sports legs, always display "Yes"
                      // For Tennis or Cricket props (non-sports) that are X vs Y, we always display "Yes"
                      const isHeadToHeadProp = !isSportsLeg && /\svs\.?\s/i.test(leg.eventTitle) && /Tennis|Cricket/i.test(leg.eventTitle);
                      const badgeOutcome = (isSportsLeg || isHeadToHeadProp) ? 'yes' : leg.outcome;
                      return (
                        <span className={`leg-outcome-badge ${badgeOutcome}`}>
                          {badgeOutcome === 'yes' ? 'Yes' : 'No'}
                        </span>
                      );
                    })()}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Total Amount Input */}
        <div className="amount-section">
          <div className="amount-header">
            <label>Stake</label>
          </div>
          <input 
            type="text" 
            className={`amount-input ${totalAmount > 0 ? 'has-value' : ''}`}
            value={totalAmount > 0 ? `$${formatNumberWithCommas(totalAmount)}` : ''}
            onChange={(e) => {
              const value = e.target.value.replace(/[$,]/g, '');
              const numValue = Number(value) || 0;
              setTotalAmount(numValue);
              
              // If we have a quote and the stake changes, mark it as modified
              if (showToWin && currentQuote && numValue !== currentQuote.total_amount) {
                setStakeModifiedAfterQuote(true);
                setShowToWin(false); // Reset quote state
              }
              
              try { posthog.capture('parlay:stake_defined', { stake: numValue, legs_count: parlayLegs.length }); } catch {}
            }}
            placeholder="$0"
          />
        </div>

        {/* To Win Section */}
        {currentQuote && showToWin && !stakeModifiedAfterQuote && (
          <div className="amount-section" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', margin: '16px 0' }}>
            <label style={{ fontSize: '16px', color: '#333', fontWeight: '600' }}>To win</label>
            <div style={{ textAlign: 'right' }}>
              <div className="amount-display-static" style={{ color: '#FF3100', margin: 0 }}>
                ${formatNumberWithCommas(currentQuote.expected_payout)}
              </div>
              <div style={{ fontSize: '14px', color: 'black', fontWeight: '600', marginTop: '4px' }}>
                ({currentQuote.total_odds.toFixed(2)}x)
              </div>
            </div>
          </div>
        )}

        {errorMsg && <div className="error-message">{errorMsg}</div>}

        <button
          className="add-to-parlay-btn"
          disabled={
            hasSelections
              ? false
              : (isLoading || totalAmount <= 0 || isQuoting || isPlacing || isInvalidParlayState)
          }
          onClick={hasSelections ? handleRemoveLegs : (showToWin && !stakeModifiedAfterQuote ? handleConfirm : handleViewOdds)}
        >
          {isLoading || isQuoting || isPlacing ? (
            <LoadingDots className="button-loading" />
          ) : hasSelections ? (
            selectedLegIndices.size === 1 ? 'Remove Leg' : 'Remove Legs'
          ) : (
            showToWin && !stakeModifiedAfterQuote ? 'Confirm Parlay' : 'View Parlay Quote'
          )}
        </button>

        {/* Terms text - only show when button is "Confirm Parlay" */}
        {!hasSelections && showToWin && !stakeModifiedAfterQuote && (
          <div className="total-amount-modal-terms-text">
            By confirming, you agree to the <span 
              className="total-amount-modal-terms-link"
              onClick={() => { try { posthog.capture('parlay:terms_clicked'); } catch {} window.open('https://docs.google.com/document/d/1ybQTWpo4lbXmDNc1tYCisBrtYXNWuiKoGW1EKIxXoo4/edit?tab=t.0', '_blank'); }}
              style={{ cursor: 'pointer', textDecoration: 'underline' }}
            >Terms of Use</span>
          </div>
        )}

        <LoginModal isOpen={isLoginModalOpen} onClose={handleLoginSuccess} />
      </div>
    </div>
  );
};

export default TotalAmountModal;